'use client';

import { CheckCircle2, XCircle, Minus } from 'lucide-react';

export interface ComparisonFeature {
  /** Feature name */
  name: string;
  /** Email Ferret value - true for check, false for X, string for text */
  emailFerret: boolean | string;
  /** Competitor value - true for check, false for X, string for text */
  competitor: boolean | string;
  /** Optional highlight for Email Ferret advantage */
  highlight?: boolean;
}

interface ComparisonTableProps {
  /** Competitor name */
  competitorName: string;
  /** Array of features to compare */
  features: ComparisonFeature[];
  /** Optional className */
  className?: string;
}

function FeatureValue({ value, isHighlight }: { value: boolean | string; isHighlight?: boolean }) {
  if (typeof value === 'boolean') {
    if (value) {
      return (
        <span className="flex items-center justify-center gap-1">
          <CheckCircle2 className="h-5 w-5 text-green-400" />
          <span className="sr-only">Yes</span>
        </span>
      );
    }
    return (
      <span className="flex items-center justify-center gap-1">
        <XCircle className="h-5 w-5 text-red-400" />
        <span className="sr-only">No</span>
      </span>
    );
  }

  if (value === '-') {
    return (
      <span className="flex items-center justify-center gap-1">
        <Minus className="h-5 w-5 text-muted-foreground" />
        <span className="sr-only">Not applicable</span>
      </span>
    );
  }

  return (
    <span className={`text-sm ${isHighlight ? 'text-green-400 font-semibold' : 'text-foreground'}`}>
      {value}
    </span>
  );
}

export function ComparisonTable({
  competitorName,
  features,
  className = '',
}: ComparisonTableProps) {
  return (
    <div className={`overflow-x-auto ${className}`}>
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="border-b border-border">
            <th className="py-4 px-4 text-foreground font-semibold text-sm">Feature</th>
            <th className="py-4 px-4 text-blue-400 font-semibold text-sm text-center min-w-[120px]">
              Email Ferret
            </th>
            <th className="py-4 px-4 text-purple-400 font-semibold text-sm text-center min-w-[120px]">
              {competitorName}
            </th>
          </tr>
        </thead>
        <tbody>
          {features.map((feature, index) => (
            <tr
              key={feature.name}
              className={`border-b border-border/50 ${
                feature.highlight ? 'bg-green-500/5' : ''
              } ${index % 2 === 0 ? 'bg-muted/30' : ''}`}
            >
              <td className="py-3 px-4 text-foreground text-sm">
                {feature.name}
                {feature.highlight && (
                  <span className="ml-2 text-xs text-green-400 font-medium">(Advantage)</span>
                )}
              </td>
              <td className="py-3 px-4 text-center">
                <FeatureValue value={feature.emailFerret} isHighlight={feature.highlight} />
              </td>
              <td className="py-3 px-4 text-center">
                <FeatureValue value={feature.competitor} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Quick comparison box for hero sections
interface QuickComparisonProps {
  competitorName: string;
  emailFerretAdvantages: string[];
  competitorLimitations: string[];
  className?: string;
}

export function QuickComparison({
  competitorName,
  emailFerretAdvantages,
  competitorLimitations,
  className = '',
}: QuickComparisonProps) {
  return (
    <div className={`bg-card rounded-lg p-6 border border-border ${className}`}>
      <h3 className="text-xl font-semibold text-foreground mb-4">Quick Comparison</h3>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-semibold text-blue-400 mb-3">Email Ferret</h4>
          <ul className="space-y-2 text-sm text-foreground">
            {emailFerretAdvantages.map((advantage) => (
              <li key={advantage} className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                <span>{advantage}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-semibold text-purple-400 mb-3">{competitorName}</h4>
          <ul className="space-y-2 text-sm text-foreground">
            {competitorLimitations.map((limitation) => (
              <li key={limitation} className="flex items-start gap-2">
                <XCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                <span>{limitation}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
